import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { resetMultiAgentForTenantChange } from "@/stores/multiAgentStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

/**
 * Drops the previous tenant's conversation state when the signed-in user or the selected
 * organization changes under a mounted route: the multi-agent stores, the cached `["multi-agent"]`
 * queries and any `?session=` / `?run=` link (which names a conversation of the OTHER tenant).
 *
 * The first resolved identity is only remembered; a reset needs a previous one to differ from.
 */
export function useTenantChangeReset(): void {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const tenantOrgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const tenantUserId = useAuthStoreV1((s) => s.user?.id ?? null);
  const prevTenantRef = useRef<string | null>(null);

  useEffect(() => {
    const key = tenantUserId && tenantOrgId ? `${tenantUserId}:${tenantOrgId}` : null;
    if (key && prevTenantRef.current && prevTenantRef.current !== key) {
      resetMultiAgentForTenantChange();
      void queryClient.removeQueries({ queryKey: ["multi-agent"] });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("run");
          next.delete("session");
          return next;
        },
        { replace: true },
      );
    }
    if (key) prevTenantRef.current = key;
  }, [tenantUserId, tenantOrgId, queryClient, setSearchParams]);
}
